<template>
    <div class="canvas-container eq-component">
        <canvas ref="canvas"></canvas>

        <div class="knob-overlay" ref="overlay">
            <div class="knob knob-bass" v-bind:style="{ left: positions.bass.x + '%', top: positions.bass.y + '%' }"
                v-on:mousedown="startDrag('bass', $event)" v-on:touchstart="startDrag('bass', $event)"
                v-on:wheel.prevent.stop="adjustBandWidth('bass', $event)"
                v-on:dblclick="resetBand('bass')">
                <div class="knob-circle"></div>
                <div class="knob-label">{{ formatBandLabel(bass, bassFrequency) }}</div>
            </div>

            <div class="knob knob-mid" v-bind:style="{ left: positions.mid.x + '%', top: positions.mid.y + '%' }"
                v-on:mousedown="startDrag('mid', $event)" v-on:touchstart="startDrag('mid', $event)"
                v-on:wheel.prevent.stop="adjustBandWidth('mid', $event)"
                v-on:dblclick="resetBand('mid')">
                <div class="knob-circle"></div>
                <div class="knob-label">{{ formatBandLabel(localMid, localMidFrequency) }}</div>
            </div>

            <div class="knob knob-treble"
                v-bind:style="{ left: positions.treble.x + '%', top: positions.treble.y + '%' }"
                v-on:mousedown="startDrag('treble', $event)" v-on:touchstart="startDrag('treble', $event)"
                v-on:wheel.prevent.stop="adjustBandWidth('treble', $event)"
                v-on:dblclick="resetBand('treble')">
                <div class="knob-circle"></div>
                <div class="knob-label">{{ formatBandLabel(treble, trebleFrequency) }}</div>
            </div>
        </div>
    </div>
</template>

<script>

export default {
    name: "EqComponent",
    props: {
        low: {
            type: Number,
            default: 0
        },
        mid: {
            type: Number,
            default: 0
        },
        high: {
            type: Number,
            default: 0
        },
        lowFrequency: {
            type: Number,
            default: 320
        },
        midFrequency: {
            type: Number,
            default: 1000
        },
        highFrequency: {
            type: Number,
            default: 3200
        },
        lowQ: {
            type: Number,
            default: 1
        },
        midQ: {
            type: Number,
            default: 1
        },
        highQ: {
            type: Number,
            default: 1
        }
    },
    emits: [
        'update:low',
        'update:mid',
        'update:high',
        'update:lowFrequency',
        'update:midFrequency',
        'update:highFrequency',
        'update:lowQ',
        'update:midQ',
        'update:highQ'
    ],
    data() {
        return {
            bass: 0,
            localMid: 0,
            treble: 0,
            bassFrequency: 320,
            localMidFrequency: 1000,
            trebleFrequency: 3200,
            bassQ: 1,
            localMidQ: 1,
            trebleQ: 1,
            dragging: null,
            dragOffsetX: 0,
            dragOffsetY: 0,
            canvasWidth: 0,
            canvasHeight: 0,
            minFrequency: 50,
            maxFrequency: 20000
        }
    },
    computed: {
        positions: function () {
            const bassX = this.frequencyToPercent(this.bassFrequency);
            const midX = this.frequencyToPercent(this.localMidFrequency);
            const trebleX = this.frequencyToPercent(this.trebleFrequency);

            const bassY = (1 - (this.bass / 12 + 1) / 2) * 100;
            const midY = (1 - (this.localMid / 12 + 1) / 2) * 100;
            const trebleY = (1 - (this.treble / 12 + 1) / 2) * 100;

            return {
                bass: { x: bassX, y: bassY },
                mid: { x: midX, y: midY },
                treble: { x: trebleX, y: trebleY }
            };
        }
    },

    watch: {
        low: {
            handler(newVal) {
                this.bass = newVal;
            },
            immediate: true
        },
        mid: {
            handler(newVal) {
                this.localMid = newVal;
            },
            immediate: true
        },
        high: {
            handler(newVal) {
                this.treble = newVal;
            },
            immediate: true
        },
        lowFrequency: {
            handler(newVal) {
                this.bassFrequency = this.clampFrequency(newVal, 320);
            },
            immediate: true
        },
        midFrequency: {
            handler(newVal) {
                this.localMidFrequency = this.clampFrequency(newVal, 1000);
            },
            immediate: true
        },
        highFrequency: {
            handler(newVal) {
                this.trebleFrequency = this.clampFrequency(newVal, 3200);
            },
            immediate: true
        },
        lowQ: {
            handler(newVal) {
                this.bassQ = this.clampQ(newVal);
            },
            immediate: true
        },
        midQ: {
            handler(newVal) {
                this.localMidQ = this.clampQ(newVal);
            },
            immediate: true
        },
        highQ: {
            handler(newVal) {
                this.trebleQ = this.clampQ(newVal);
            },
            immediate: true
        }
    },

    methods: {
        formatDb: function (value) {
            return (value > 0 ? '+' : '') + value.toFixed(1) + 'dB';
        },

        formatFrequency: function (frequency) {
            if (frequency >= 1000) {
                const kilohertz = frequency / 1000;
                return (kilohertz >= 10 ? kilohertz.toFixed(0) : kilohertz.toFixed(1)) + 'kHz';
            }

            return Math.round(frequency) + 'Hz';
        },

        formatBandLabel: function (gain, frequency) {
            return this.formatDb(gain) + ' · ' + this.formatFrequency(frequency);
        },

        clampFrequency: function (frequency, fallback) {
            const numericFrequency = Number(frequency);
            const safeFrequency = Number.isFinite(numericFrequency) ? numericFrequency : fallback;
            return Math.max(this.minFrequency, Math.min(this.maxFrequency, safeFrequency));
        },

        clampQ: function (q) {
            const numericQ = Number(q);
            const safeQ = Number.isFinite(numericQ) ? numericQ : 1;
            return Math.max(0.25, Math.min(8, safeQ));
        },

        frequencyToPercent: function (frequency) {
            const minLog = Math.log10(this.minFrequency);
            const maxLog = Math.log10(this.maxFrequency);
            const frequencyLog = Math.log10(this.clampFrequency(frequency, this.minFrequency));
            return ((frequencyLog - minLog) / (maxLog - minLog)) * 100;
        },

        percentToFrequency: function (percent) {
            const clampedPercent = Math.max(0, Math.min(100, percent));
            const minLog = Math.log10(this.minFrequency);
            const maxLog = Math.log10(this.maxFrequency);
            return Math.round(Math.pow(10, minLog + (clampedPercent / 100) * (maxLog - minLog)));
        },

        calculateFrequencyResponse: function (freq, bass, mid, treble, bassFreq, midFreq, trebleFreq, bassQ, midQ, trebleQ) {
            const bassGain = bass * Math.exp(-0.5 * Math.pow(Math.log(freq / bassFreq) * bassQ, 2));
            const midGain = mid * Math.exp(-0.5 * Math.pow(Math.log(freq / midFreq) * midQ, 2));
            const trebleGain = treble * Math.exp(-0.5 * Math.pow(Math.log(freq / trebleFreq) * trebleQ, 2));

            return bassGain + midGain + trebleGain;
        },

        drawEqualizer: function () {
            const canvas = this.$refs.canvas;
            if (!canvas) return;

            const container = canvas.parentElement;
            if (!container) return;

            const rect = container.getBoundingClientRect();
            canvas.width = rect.width;
            canvas.height = rect.height;

            this.canvasWidth = canvas.width;
            this.canvasHeight = canvas.height;

            const ctx = canvas.getContext('2d');
            const width = canvas.width;
            const height = canvas.height;

            ctx.fillStyle = '#1E1E1E';
            ctx.fillRect(0, 0, width, height);

            ctx.strokeStyle = '#2a2a2a';
            ctx.lineWidth = 1;

            for (let i = 0; i <= 4; i++) {
                const y = (height / 4) * i;
                ctx.beginPath();
                ctx.moveTo(0, y);
                ctx.lineTo(width, y);
                ctx.stroke();
            }

            const freqs = [50, 100, 200, 500, 1000, 2000, 5000, 10000, 20000];
            freqs.forEach(function (freq) {
                const x = (Math.log10(freq) - 1.7) / 2.6 * width;
                ctx.beginPath();
                ctx.moveTo(x, 0);
                ctx.lineTo(x, height);
                ctx.stroke();
            });

            ctx.fillStyle = '#666';
            ctx.font = Math.max(10, width / 80) + 'px Arial';
            ctx.textAlign = 'center';
            freqs.forEach(function (freq) {
                const x = (Math.log10(freq) - 1.7) / 2.6 * width;
                const label = freq >= 1000 ? (freq / 1000) + 'k' : freq.toString();
                ctx.fillText(label, x, height - 5);
            });

            ctx.textAlign = 'right';
            for (let db = -12; db <= 12; db += 6) {
                const y = height / 2 - (db / 12) * (height / 2 - 20);
                ctx.fillText((db > 0 ? '+' : '') + db, width * 0.05, y + 3);
            }

            ctx.beginPath();
            ctx.strokeStyle = '#999999';
            ctx.lineWidth = 4;
            ctx.shadowBlur = 2;
            ctx.shadowColor = '#999999';

            const self = this;
            for (let x = 0; x < width; x++) {
                const freq = Math.pow(10, 1.7 + (x / width) * 2.6);
                const gain = self.calculateFrequencyResponse(
                    freq,
                    self.bass,
                    self.localMid,
                    self.treble,
                    self.bassFrequency,
                    self.localMidFrequency,
                    self.trebleFrequency,
                    self.bassQ,
                    self.localMidQ,
                    self.trebleQ
                );
                const y = height / 2 - (gain / 12) * (height / 2 - 20);

                if (x === 0) {
                    ctx.moveTo(x, y);
                } else {
                    ctx.lineTo(x, y);
                }
            }

            ctx.stroke();
            ctx.shadowBlur = 0;

            ctx.strokeStyle = '#2a2a2a';
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.moveTo(0, height / 2);
            ctx.lineTo(width, height / 2);
            ctx.stroke();
        },

        startDrag: function (band, e) {
            e.preventDefault();
            this.dragging = band;

            const pointer = e.touches ? e.touches[0] : e;
            const knobRect = e.currentTarget.getBoundingClientRect();
            this.dragOffsetX = pointer.clientX - (knobRect.left + knobRect.width / 2);
            this.dragOffsetY = pointer.clientY - (knobRect.top + knobRect.height / 2);
        },

        handleMove: function (e) {
            if (!this.dragging) return;

            const overlay = this.$refs.overlay;
            if (!overlay) return;

            const rect = overlay.getBoundingClientRect();
            const pointer = e.touches ? e.touches[0] : e;
            const x = pointer.clientX - rect.left - this.dragOffsetX;
            const y = pointer.clientY - rect.top - this.dragOffsetY;
            const percentX = (x / rect.width) * 100;
            const percentY = (y / rect.height) * 100;

            const frequency = this.percentToFrequency(percentX);
            const db = (1 - percentY / 100) * 24 - 12;
            const clampedDb = Math.round(Math.max(-12, Math.min(12, db)) * 2) / 2;

            // Update local value and emit to parent
            if (this.dragging === 'bass') {
                this.bass = clampedDb;
                this.bassFrequency = frequency;
                this.$emit('update:low', clampedDb);
                this.$emit('update:lowFrequency', frequency);
            } else if (this.dragging === 'mid') {
                this.localMid = clampedDb;
                this.localMidFrequency = frequency;
                this.$emit('update:mid', clampedDb);
                this.$emit('update:midFrequency', frequency);
            } else if (this.dragging === 'treble') {
                this.treble = clampedDb;
                this.trebleFrequency = frequency;
                this.$emit('update:high', clampedDb);
                this.$emit('update:highFrequency', frequency);
            }

            // Redraw the equalizer curve
            this.drawEqualizer();
        },

        stopDrag: function () {
            this.dragging = null;
        },

        adjustBandWidth: function (band, event) {
            const delta = event.deltaY < 0 ? 0.1 : -0.1;

            if (band === 'bass') {
                this.bassQ = this.clampQ(Math.round((this.bassQ + delta) * 10) / 10);
                this.$emit('update:lowQ', this.bassQ);
            } else if (band === 'mid') {
                this.localMidQ = this.clampQ(Math.round((this.localMidQ + delta) * 10) / 10);
                this.$emit('update:midQ', this.localMidQ);
            } else if (band === 'treble') {
                this.trebleQ = this.clampQ(Math.round((this.trebleQ + delta) * 10) / 10);
                this.$emit('update:highQ', this.trebleQ);
            }

            this.drawEqualizer();
        },

        reset: function () {
            this.bass = 0;
            this.localMid = 0;
            this.treble = 0;
            this.bassFrequency = 320;
            this.localMidFrequency = 1000;
            this.trebleFrequency = 3200;
            this.bassQ = 1;
            this.localMidQ = 1;
            this.trebleQ = 1;

            this.$emit('update:low', 0);
            this.$emit('update:mid', 0);
            this.$emit('update:high', 0);
            this.$emit('update:lowFrequency', 320);
            this.$emit('update:midFrequency', 1000);
            this.$emit('update:highFrequency', 3200);
            this.$emit('update:lowQ', 1);
            this.$emit('update:midQ', 1);
            this.$emit('update:highQ', 1);

            // Redraw the equalizer curve
            this.drawEqualizer();
        },

        resetBand: function (band) {
            if (band === 'bass') {
                this.bass = 0;
                this.bassFrequency = 320;
                this.bassQ = 1;
                this.$emit('update:low', 0);
                this.$emit('update:lowFrequency', 320);
                this.$emit('update:lowQ', 1);
            } else if (band === 'mid') {
                this.localMid = 0;
                this.localMidFrequency = 1000;
                this.localMidQ = 1;
                this.$emit('update:mid', 0);
                this.$emit('update:midFrequency', 1000);
                this.$emit('update:midQ', 1);
            } else if (band === 'treble') {
                this.treble = 0;
                this.trebleFrequency = 3200;
                this.trebleQ = 1;
                this.$emit('update:high', 0);
                this.$emit('update:highFrequency', 3200);
                this.$emit('update:highQ', 1);
            }
            this.drawEqualizer();
        },

        handleResize: function () {
            this.drawEqualizer();
        }
    },

    mounted: function () {
        this.drawEqualizer();
        window.addEventListener('resize', this.handleResize);
        window.addEventListener('mousemove', this.handleMove);
        window.addEventListener('mouseup', this.stopDrag);
        window.addEventListener('touchmove', this.handleMove);
        window.addEventListener('touchend', this.stopDrag);
    },

    beforeUnmount: function () {
        window.removeEventListener('resize', this.handleResize);
        window.removeEventListener('mousemove', this.handleMove);
        window.removeEventListener('mouseup', this.stopDrag);
        window.removeEventListener('touchmove', this.handleMove);
        window.removeEventListener('touchend', this.stopDrag);
    }
}

</script>

<style>
.canvas-container {
    width: 100%;
    height: 100%;
    position: relative;
    overflow: hidden;
}

canvas {
    width: 100%;
    height: 100%;
    display: block;
}

.knob-overlay {
    position: absolute;
    top: 20px;
    left: 20px;
    right: 20px;
    bottom: 20px;
    pointer-events: none;
}

.knob {
    position: absolute;
    pointer-events: auto;
    cursor: grab;
    transform: translate(-50%, -50%);
    transition: transform 0.1s ease;
}

.knob:active {
    cursor: grabbing;
}

.knob:hover .knob-circle {
    transform: scale(1.15);
}

.knob-circle {
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 50%;
    border: 4px solid #4b4b4b;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
    transition: transform 0.2s ease;
    background-color: #2F2F2F;
}
.knob-label {
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    margin-top: 8px;
    padding: 4px 12px;
    background: #2F2F2F;
    border-radius: 6px;
    white-space: nowrap;
    font-weight: bold;
    font-size: 0.8rem;
    pointer-events: none;
}
</style>
